import { Button, Grid } from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useReactToPrint } from "react-to-print";
import { useTranslation } from 'react-i18next';
import { LoansContext } from '../../context/LoansContext';
import CloseIcon from '../../assets/Close-Icon-lg.svg'
import InfoIcon from '../../assets/InfoIconBlack.svg'
import ToorakLogo from '../../assets/Toorak_Logo.svg'
import ExportIcon from '../../assets/ExportIcon.svg'
import KDMModal from './KDMModal';
import { useParams } from 'react-router-dom';
import { formatValueByType } from '../../utils/Common';
import { orgBucketResponse } from '../../services/network';
import { SizingTypeValue } from '../../types/enums';

interface KDMWrapperProps {}


type ParamType = {
  loanType: string | undefined;
  loanId: string | undefined;
  propType: string | undefined;
};

const resolveLoanResultData = (opts: { rulesResponse: any; assignedPartner: any; loanId: string }) => {
  const { rulesResponse, assignedPartner, loanId } = opts;
  if (rulesResponse?.tpResultMap?.[assignedPartner]) {
    return rulesResponse?.tpResultMap?.[assignedPartner]?.loanResult?.[loanId];
  }
  if (rulesResponse?.loanResult) {
    return rulesResponse?.loanResult[loanId];
  }
  if (rulesResponse) {
    return rulesResponse?.[assignedPartner || Object.keys(rulesResponse)[0]]?.loanResult?.[loanId];
  }
  return {};
};

const resolvePropertiesResults = (opts: { rulesResponse: any; assignedPartner: any }) => {
  const { rulesResponse, assignedPartner } = opts;
  if (rulesResponse?.tpResultMap?.[assignedPartner]?.propertiesResults?.["1"]) {
    return rulesResponse?.tpResultMap?.[assignedPartner]?.propertiesResults?.["1"];
  }
  if (rulesResponse?.propertiesResults) {
    return rulesResponse?.propertiesResults?.["0"];
  }
  if (rulesResponse) {
    return rulesResponse?.[assignedPartner || Object.keys(rulesResponse)[0]]?.propertiesResults?.["1"];
  }
  return {};
};

const resolveOriginator = async (opts: { originator: any; orgId: any }) => {
  const { originator, orgId } = opts;
  if (!orgId || originator) {
    return originator;
  }
  const response = await orgBucketResponse(orgId);
  let resolved = originator;
  if (response?.data?.accounts?.length) {
    resolved = response?.data?.accounts?.find((ele: any) => ele?.partyId === orgId);
  }
  if (!resolved && response?.data?.customers?.length) {
    resolved = response?.data?.customers?.find((ele: any) => ele?.partyId === orgId);
  }
  return resolved;
};

const mergeSizingData = (opts: { loanDetails: any; quickLoanSizingData: any; detailedLoanSizingData: any }) => {
  const { loanDetails, quickLoanSizingData, detailedLoanSizingData } = opts;
  let merged: any = { ...loanDetails };
  if (merged?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING) {
    merged = { ...merged, ...quickLoanSizingData };
  } else if (merged?.sizingType === SizingTypeValue?.DETAILED_LOAN_SIZING) {
    merged = { ...merged, ...detailedLoanSizingData };
  }
  return merged;
};

const findSelectedBuyDownOption = (opts: { buyDownOptions: any; currentSelectedInterestRate: any }) => {
  const { buyDownOptions, currentSelectedInterestRate } = opts;
  return buyDownOptions?.find((item: any) => {
    const itemInterestRate = item?.interestRate
      ? Number.parseFloat(item.interestRate) * 100
      : null;
    const selectedInterestRate = currentSelectedInterestRate?.interestRate
      ? Number.parseFloat(currentSelectedInterestRate?.interestRate.replace("%", ""))
      : null;

    if (itemInterestRate === null || selectedInterestRate === null) {
      return false;
    }
    return itemInterestRate.toFixed(4) === selectedInterestRate.toFixed(4);
  });
};

const KDMWrapper = (props: KDMWrapperProps) => {
  const { t } = useTranslation();
  const loanContextData = useContext(LoansContext);
  const [showKDMBody, setShowKDMBody] = useState<boolean>(false)
  const [originatorInfo, setOriginatorInfo] = useState<any>(null)
  const [data, setData] = React.useState<{
    loanDetails: any;
    propertyDetails: any;
    ruleResult: any;
    propertiesResults: any;
    loanStage: string;
    loanType: string | undefined;
    originatorInfo: any;
    selectedBuyDownOptions: any;
    leverageAndPricingData: any;
    templateResponseData: any;
  } | null>(null);

  const params = useParams<ParamType>();
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef
  });

  const { loanType, loanId } = params;

  const handleShowKDMBody = (status: boolean) => {
    setShowKDMBody(status)
  }

  const openGoogleMap = (label: string) => {
    const query = `http://maps.google.com/?q=${label}`;
    window.open(query);
  };

  useEffect(() => {
    if (loanId) {
      init(loanId)
    }

  }, [
      loanContextData, 
      loanId, 
      originatorInfo,
      loanContextData?.loanDetails,
      loanContextData?.sizingType,
      loanContextData?.currentSelectedInterestRate      
    ])

  const init = async (loanId: string) => {
    const assignedPartner = loanContextData?.assignedPartner
    const rulesResponse = loanContextData?.rulesResponse ? loanContextData?.rulesResponse || {} : loanContextData?.loanDetails?.rulesResponse || {}
    const loanResultData = resolveLoanResultData({ rulesResponse, assignedPartner, loanId })
    const propertiesResults = resolvePropertiesResults({ rulesResponse, assignedPartner })
    const originator = await resolveOriginator({
      originator: originatorInfo ?? null,
      orgId: loanContextData?.loanDetails?.orgId,
    })
    setOriginatorInfo(originator)
    const newDataToBeReffered: any = mergeSizingData({
      loanDetails: loanContextData?.loanDetails,
      quickLoanSizingData: loanContextData?.quickLoanSizingData,
      detailedLoanSizingData: loanContextData?.detailedLoanSizingData,
    });
    const propertyDetails = loanContextData?.propertyDetails?.length ? loanContextData?.propertyDetails : newDataToBeReffered?.propertyDetails

    const currentSelectedInterestRate =
      newDataToBeReffered?.currentSelectedInterestRate ?? loanContextData?.currentSelectedInterestRate
    const buyDownOptions =
      loanResultData?.buyDownOptions;
    const selectedBuyDownOptions = findSelectedBuyDownOption({ buyDownOptions, currentSelectedInterestRate });
    setData({
      loanDetails: newDataToBeReffered,
      propertyDetails,
      ruleResult: loanResultData,
      propertiesResults: propertiesResults,
      loanStage: "FES",
      loanType,
      originatorInfo: originator,
      selectedBuyDownOptions,
      leverageAndPricingData: loanContextData?.leverageAndPricingData,
      templateResponseData: loanContextData?.templateResponseData ?? {}
    });
  }
  return (
    <div>
      <Grid item className="gray-border">
        <Button
          className="mr-12"
          variant="outlined"
          onClick={() => handleShowKDMBody(true)}
          data-testid="key-deal-metrics-button"
        >
          {t("kdm")}
        </Button>
        {showKDMBody ?
          <KDMModal
            header={t("kdm")}
            handleShowKDMBody={handleShowKDMBody}
            icons={{ CloseIcon, InfoIcon, ToorakLogo, ExportIcon }}
            data={data}
            config={{
              formatValueByType: formatValueByType,
              openGoogleMap: openGoogleMap,
              componentRef: contentRef,
              reactToPrintFn: reactToPrintFn,
              loanId: loanId
            }}
          />
          : null}
      </Grid>
    </div>
  )
}

export default KDMWrapper
